import { CollapsibleFilters } from '../../../components/common/CollapsibleFilters';
// pages/hr/recruiting/CampaignListPage.tsx
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Typography,
  Button,
  Card,
  Chip,
  IconButton,
  Dropdown,
  MenuButton,
  Menu,
  MenuItem,
  Modal,
  ModalDialog,
  ModalClose,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Stack,
  Tabs,
  TabList,
  Tab,
  LinearProgress,
} from '@mui/joy';
import { PageHelpButton } from '../../../components/knowledge-base/PageHelpButton';
import { ResponsiveTable } from '../../../components/common/ResponsiveTable';
import {
  Plus as AddOutlined,
  DotsThreeVertical as MoreVertOutlined,
  Play as PlayArrowOutlined,
  Pause as PauseOutlined,
  Stop as StopOutlined,
  PencilSimple as EditOutlined,
  Eye as VisibilityOutlined,
  Trash as DeleteOutlined,
  Users as PeopleOutlined,
  TrendUp as TrendingUpOutlined,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import {
  useRecruitingCampaigns,
  useCreateRecruitingCampaign,
  useLaunchCampaign,
  usePauseCampaign,
  useCloseCampaign,
  useDeleteRecruitingCampaign,
} from '../../../hooks/useHr';
import type {
  RecruitingCampaignListItem,
  CampaignStatus,
  CreateCampaignRequest,
} from '../../../types/hr';
import { apiClient } from '../../../lib/api';
import { applyPrefill } from '../../../utils/prefill';
import toast from 'react-hot-toast';
import { useDebounce } from '../../../hooks/useDebounce';

const statusColors: Record<CampaignStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  closed: 'danger',
};

const statusLabels: Record<CampaignStatus, string> = {
  draft: 'Draft',
  active: 'Active',
  paused: 'Paused',
  closed: 'Closed',
};

export const CampaignListPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<CampaignStatus | 'all'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [page, setPage] = useState(1); const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 250);
  const { data: response, isLoading, error, refetch } = useRecruitingCampaigns(statusFilter === 'all' ? undefined : statusFilter, page, debouncedSearch);
  const campaigns = response?.results || [];
  const createCampaign = useCreateRecruitingCampaign();
  const launchCampaign = useLaunchCampaign();
  const pauseCampaign = usePauseCampaign();
  const closeCampaign = useCloseCampaign();
  const deleteCampaign = useDeleteRecruitingCampaign();

  const [createForm, setCreateForm] = useState<CreateCampaignRequest>({
    name: '',
    role_title: '',
    location: '',
    description: '',
    hiring_goal_per_month: 1,
  });

  // Hiring happens where the company is, and "City, ST" is exactly the shape
  // the apply page and the recruiting texts render.
  const { data: organization } = useQuery({
    queryKey: ['organization'],
    queryFn: async () => {
      const response = await apiClient.get<{
        main_office_city?: string;
        main_office_state?: string;
      }>('/api/organizations/current/');
      return response.data;
    },
  });

  const mainOfficeLocation = [organization?.main_office_city, organization?.main_office_state]
    .filter(Boolean)
    .join(', ');

  // Seeded rather than hardcoded into the initial state so it survives the
  // organization query resolving after the modal is already open - and it only
  // fills a location the user has left blank.
  useEffect(() => {
    if (!createModalOpen || !mainOfficeLocation) return;
    setCreateForm(prev => applyPrefill(prev, { location: mainOfficeLocation }));
  }, [createModalOpen, mainOfficeLocation]);

  const handleCreate = async () => {
    try {
      const campaign = await createCampaign.mutateAsync(createForm);
      setCreateModalOpen(false);
      setCreateForm({
        name: '',
        role_title: '',
        location: '',
        description: '',
        hiring_goal_per_month: 1,
      });
      toast.success('Campaign created successfully');
      navigate(`/hr/recruiting/${campaign.id}`);
    } catch {
      toast.error('Failed to create campaign');
    }
  };

  const handleLaunch = async (id: string) => {
    try {
      await launchCampaign.mutateAsync(id);
      toast.success('Campaign launched');
    } catch {
      toast.error('Failed to launch campaign');
    }
  };

  const handlePause = async (id: string) => {
    try {
      await pauseCampaign.mutateAsync(id);
      toast.success('Campaign paused');
    } catch {
      toast.error('Failed to pause campaign');
    }
  };

  const handleClose = async (id: string) => {
    try {
      await closeCampaign.mutateAsync(id);
      toast.success('Campaign closed');
    } catch {
      toast.error('Failed to close campaign');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await deleteCampaign.mutateAsync(id);
      toast.success('Campaign deleted');
    } catch {
      toast.error('Failed to delete campaign');
    }
  };

  const getHiringProgress = (campaign: RecruitingCampaignListItem) => {
    const goal = campaign.hiring_goal_per_month || 1;
    const progress = (campaign.hired_count / goal) * 100;
    return Math.min(progress, 100);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' } }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Recruiting Campaigns</Typography>
            <PageHelpButton />
          </Box>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Manage your hiring campaigns and track candidate pipelines
          </Typography>
        </Box>
        <Button
          startDecorator={<AddOutlined />}
          onClick={() => setCreateModalOpen(true)}
        >
          New Campaign
        </Button>
      </Stack>

      <Tabs
        value={statusFilter}
        onChange={(_, value) => { setStatusFilter(value as CampaignStatus | 'all'); setPage(1); }}
        sx={{ mb: 2 }}
      >
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: '1 1 30%', sm: '0 0 auto' } } }}>
          <Tab value="all">All</Tab>
          <Tab value="active">Active</Tab>
          <Tab value="draft">Draft</Tab>
          <Tab value="paused">Paused</Tab>
          <Tab value="closed">Closed</Tab>
        </TabList>
      </Tabs>

      <CollapsibleFilters><Input aria-label="Search campaigns" placeholder="Search campaigns, roles or locations" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} sx={{ mb: 2 }} /></CollapsibleFilters>
      <Stack direction="row" sx={{ gap: 1, mb: 2 }}><Button variant="outlined" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</Button><Typography>Page {page} · {response?.count || 0} campaigns</Typography><Button variant="outlined" disabled={!response?.next} onClick={() => setPage(p => p + 1)}>Next</Button></Stack>
      {error ? <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Could not load campaigns.</Alert> : isLoading ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography>Loading campaigns...</Typography>
          </Box>
        </Card>
      ) : campaigns.length === 0 ? (
        <Card>
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography level="body-lg" sx={{ mb: 1 }}>No campaigns found</Typography>
            <Typography level="body-sm" sx={{ color: 'neutral.500', mb: 2 }}>
              Create your first recruiting campaign to start hiring
            </Typography>
            <Button
              startDecorator={<AddOutlined />}
              onClick={() => setCreateModalOpen(true)}
            >
              Create Campaign
            </Button>
          </Box>
        </Card>
      ) : (
        <Card>
          <ResponsiveTable<RecruitingCampaignListItem>
            rows={campaigns}
            getRowKey={(c) => c.id}
            columns={[
              {
                key: 'campaign',
                sortValue: (row) => row.name,
                header: 'Campaign',
                mobileVariant: 'header',
                render: (campaign) => (
                  <Box sx={{ minWidth: 0 }}>
                    <Typography level="title-sm" sx={{ wordBreak: 'break-word' }}>{campaign.name}</Typography>
                    <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                      {campaign.role_title} {campaign.location && `• ${campaign.location}`}
                    </Typography>
                  </Box>
                ),
              },
              {
                key: 'status',
                sortValue: (row) => row.status,
                header: 'Status',
                render: (campaign) => (
                  <Chip
                    size="sm"
                    variant="soft"
                    color={statusColors[campaign.status]}
                  >
                    {statusLabels[campaign.status]}
                  </Chip>
                ),
              },
              {
                key: 'candidates',
                sortValue: (row) => row.candidate_count,
                header: 'Candidates',
                render: (campaign) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PeopleOutlined size={16} color="var(--joy-palette-neutral-500)" />
                    <Typography level="body-sm">{campaign.candidate_count}</Typography>
                  </Box>
                ),
              },
              {
                key: 'progress',
                sortValue: (row) => row.hired_count,
                header: 'Progress',
                render: (campaign) => (
                  <Box sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography level="body-xs">
                        {campaign.hired_count} / {campaign.hiring_goal_per_month} hired
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'neutral.500' }}>
                        {getHiringProgress(campaign).toFixed(0)}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      determinate
                      value={getHiringProgress(campaign)}
                      color={getHiringProgress(campaign) >= 100 ? 'success' : 'primary'}
                      sx={{ height: 6 }}
                    />
                  </Box>
                ),
              },
              {
                key: 'pipeline',
                header: 'Pipeline',
                render: (campaign) => (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="sm" variant="outlined" color="warning">
                      {campaign.qualified_count} qualified
                    </Chip>
                    <Chip size="sm" variant="outlined" color="primary">
                      {campaign.scheduled_count} scheduled
                    </Chip>
                  </Box>
                ),
              },
            ]}
            rowActions={(campaign) => (
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: { xs: 'flex-end', md: 'flex-start' } }}>
                {/* Draft campaign actions - more prominent */}
                {campaign.status === 'draft' && (
                  <>
                    <Button
                      size="sm"
                      variant="outlined"
                      color="primary"
                      startDecorator={<EditOutlined />}
                      onClick={() => navigate(`/hr/recruiting/${campaign.id}/edit`)}
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      color="success"
                      startDecorator={<PlayArrowOutlined />}
                      onClick={() => handleLaunch(campaign.id)}
                      loading={launchCampaign.isPending}
                    >
                      Launch
                    </Button>
                  </>
                )}
                {campaign.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outlined"
                    color="warning"
                    startDecorator={<PauseOutlined />}
                    onClick={() => handlePause(campaign.id)}
                    loading={pauseCampaign.isPending}
                  >
                    Pause
                  </Button>
                )}
                {campaign.status === 'paused' && (
                  <Button
                    size="sm"
                    color="success"
                    startDecorator={<PlayArrowOutlined />}
                    onClick={() => handleLaunch(campaign.id)}
                    loading={launchCampaign.isPending}
                  >
                    Resume
                  </Button>
                )}
                <Dropdown>
                  <MenuButton
                    slots={{ root: IconButton }}
                    slotProps={{ root: { size: 'sm', variant: 'plain' } }}
                  >
                    <MoreVertOutlined />
                  </MenuButton>
                  <Menu placement="bottom-end">
                    <MenuItem onClick={() => navigate(`/hr/recruiting/${campaign.id}`)}>
                      <VisibilityOutlined style={{ marginRight: 8 }} /> View Details
                    </MenuItem>
                    <MenuItem onClick={() => navigate(`/hr/recruiting/${campaign.id}/pipeline`)}>
                      <TrendingUpOutlined style={{ marginRight: 8 }} /> Pipeline
                    </MenuItem>
                    {campaign.status !== 'draft' && (
                      <MenuItem onClick={() => navigate(`/hr/recruiting/${campaign.id}/edit`)}>
                        <EditOutlined style={{ marginRight: 8 }} /> Edit
                      </MenuItem>
                    )}
                    {campaign.status !== 'closed' && (
                      <MenuItem onClick={() => handleClose(campaign.id)} color="danger">
                        <StopOutlined style={{ marginRight: 8 }} /> Close
                      </MenuItem>
                    )}
                    {campaign.status === 'draft' && (
                      <MenuItem onClick={() => handleDelete(campaign.id)} color="danger">
                        <DeleteOutlined style={{ marginRight: 8 }} /> Delete
                      </MenuItem>
                    )}
                  </Menu>
                </Dropdown>
              </Box>
            )}
          />
        </Card>
      )}

      {/* Create Campaign Modal */}
      <Modal open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <ModalDialog sx={{ minWidth: { xs: 0, sm: 500 }, width: { xs: 'auto', sm: 500 }, maxHeight: '90vh', overflowY: 'auto' }}>
          <ModalClose />
          <Typography level="h4">Create Recruiting Campaign</Typography>

          <Stack spacing={2} sx={{ mt: 2 }}>
            <FormControl required>
              <FormLabel>Campaign Name</FormLabel>
              <Input
                placeholder="e.g., Q1 Roofing Crew Expansion"
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              />
            </FormControl>

            <FormControl required>
              <FormLabel>Role Title</FormLabel>
              <Input
                placeholder="e.g., Roofing Technician"
                value={createForm.role_title}
                onChange={(e) => setCreateForm({ ...createForm, role_title: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Location</FormLabel>
              <Input
                placeholder="e.g., Dallas, TX"
                value={createForm.location}
                onChange={(e) => setCreateForm({ ...createForm, location: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Describe the role and requirements..."
                minRows={3}
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Monthly Hiring Goal</FormLabel>
              <Input
                type="number"
                slotProps={{ input: { min: 1 } }}
                value={createForm.hiring_goal_per_month}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    hiring_goal_per_month: parseInt(e.target.value) || 1,
                  })
                }
              />
            </FormControl>

            <Stack
              direction={{ xs: 'column-reverse', sm: 'row' }}
              spacing={1.5}
              sx={{ justifyContent: 'flex-end', mt: 2 }}
            >
              <Button variant="outlined" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                loading={createCampaign.isPending}
                disabled={!createForm.name || !createForm.role_title}
              >
                Create Campaign
              </Button>
            </Stack>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default CampaignListPage;
